import { useMemo } from "react";
import type { useIssues } from "../../controllers/useIssues";
import { ISSUE_STATUS_CATEGORY } from "../../models/project";

// Lightweight day-scaled Gantt/timeline for a project: sprint bars (start→end) and issue bars
// (createdAt→dueDate), positioned on a shared absolute date axis. Read-only overview (간트); reuses
// the sprints + issues already loaded by useIssues.

const DAY = 86_400_000;
const CAT_COLOR: Record<string, string> = { TODO: "#98A2B3", IN_PROGRESS: "#2E90FA", DONE: "#12B76A" };
const SPRINT_COLOR: Record<string, string> = { FUTURE: "#98A2B3", ACTIVE: "#2E90FA", CLOSED: "#12B76A" };

const t = (s?: string | null) => {
  if (!s) return NaN;
  const n = new Date(s).getTime();
  return Number.isNaN(n) ? NaN : n;
};

export function GanttView({ issues }: { issues: ReturnType<typeof useIssues> }) {
  const rows = useMemo(() => {
    const sprintRows = issues.sprints
      .map((s) => ({ kind: "sprint" as const, id: s.id, label: s.name, start: t(s.startDate), end: t(s.endDate), color: SPRINT_COLOR[s.status] ?? "#98A2B3", meta: s.status }))
      .filter((r) => r.start && r.end && !Number.isNaN(r.start) && !Number.isNaN(r.end));
    const issueRows = issues.issues
      .filter((i) => i.dueDate)
      .map((i) => {
        const end = t(i.dueDate);
        let start = t(i.createdAt);
        if (Number.isNaN(start) || start > end) start = end - DAY; // guard: 1-day bar
        return { kind: "issue" as const, id: i.id, label: `${i.key} ${i.summary}`, start, end, color: CAT_COLOR[ISSUE_STATUS_CATEGORY[i.status]], meta: i.status };
      })
      .filter((r) => !Number.isNaN(r.end));
    return { sprintRows, issueRows };
  }, [issues.sprints, issues.issues]);

  const all = [...rows.sprintRows, ...rows.issueRows];
  const { lo, hi, months } = useMemo(() => {
    if (!all.length) return { lo: 0, hi: 0, months: [] as { label: string; left: number; width: number }[] };
    let mn = Math.min(...all.map((r) => r.start));
    let mx = Math.max(...all.map((r) => r.end));
    mn -= DAY * 3;
    mx += DAY * 3;
    const span = mx - mn || DAY;
    const ms: { label: string; left: number; width: number }[] = [];
    const d = new Date(mn);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    while (d.getTime() < mx) {
      const s = Math.max(d.getTime(), mn);
      const nx = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
      const e = Math.min(nx, mx);
      ms.push({ label: `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}`, left: ((s - mn) / span) * 100, width: ((e - s) / span) * 100 });
      d.setMonth(d.getMonth() + 1);
    }
    return { lo: mn, hi: mx, months: ms };
  }, [all]);

  if (!all.length) {
    return <div className="sdui-list-empty">타임라인에 표시할 스프린트·마감일이 없습니다. 스프린트를 만들거나 이슈에 마감일을 지정하세요.</div>;
  }

  const span = hi - lo || DAY;
  const pos = (start: number, end: number) => ({ left: `${((start - lo) / span) * 100}%`, width: `${(Math.max(end - start, DAY) / span) * 100}%` });

  const Row = ({ r, onClick }: { r: (typeof all)[number]; onClick?: () => void }) => (
    <div className="gantt-row">
      <div className="gantt-label" title={r.label}>{r.label}</div>
      <div className="gantt-track">
        <button className="gantt-bar" style={{ ...pos(r.start, r.end), background: r.color }} title={`${r.label} · ${r.meta}`} onClick={onClick} />
      </div>
    </div>
  );

  return (
    <div className="gantt">
      <div className="gantt-scroll">
        <div className="gantt-inner">
          <div className="gantt-row gantt-axis">
            <div className="gantt-label" />
            <div className="gantt-track">
              {months.map((m, i) => (
                <div key={i} className="gantt-month" style={{ left: `${m.left}%`, width: `${m.width}%` }}>{m.label}</div>
              ))}
            </div>
          </div>

          {rows.sprintRows.length > 0 && <div className="gantt-section">스프린트</div>}
          {rows.sprintRows.map((r) => <Row key={r.id} r={r} />)}

          {rows.issueRows.length > 0 && <div className="gantt-section">이슈 (생성일 → 마감일)</div>}
          {rows.issueRows.map((r) => <Row key={r.id} r={r} onClick={() => issues.openIssue(r.id)} />)}
        </div>
      </div>
    </div>
  );
}
